import { applySpecification, Component, ContainerSpecification } from '@1x2network/pixi_assets'
import { JackpotWidget, JackpotWidgetSpec } from './JackpotWidget'
import { Container, IPointData } from 'pixi.js'
import { BasicLayout, Orientation, OrientationMap, Orientor } from '@1x2network/layout'
import { PrizeConfiguration, PrizeKey } from '../../model/SetupModel'
import gsap from 'gsap'
/**
 * Group of jackpot widgets to display jackpot awards
 * @author Jordan Harrison - <jordan.harrison@1x2network.com>
 * @version 1.0.0
 */

export class JackpotWidgetGroup extends Component<JackpotWidgetGroupSpec> {
    private jackpots: JackpotRecord<JackpotWidget>
    private landed: JackPotKey[] = []

    /** get the tint for a highlight */
    get highlight() {
        return this.specification.highlight
    }

    /** get the tint for a fade */
    get fade() {
        return this.specification.fadeTint
    }

    constructor(parent: Container, layout: BasicLayout) {
        super({ spec: 'jackpotWidgetGroup', parent })

        this.jackpots = {
            mega: new JackpotWidget(this.specification.mega, this),
            major: new JackpotWidget(this.specification.major, this),
            minor: new JackpotWidget(this.specification.minor, this),
            mini: new JackpotWidget(this.specification.mini, this),
        }
        new Orientor({ property: this.specification.transforms, layout, onOrient: (active) => this.reposition(active) })
    }

    /**
     * update the current stake for each jackpot widget
     * @param stake new stake
     */
    updateStake(stake: number) {
        const keys = Object.keys(this.jackpots)
        for (const key of keys) {
            const jackpotKey = key as keyof JackpotRecord<unknown>
            const widget = this.jackpots[jackpotKey]

            widget.stake = stake
        }
    }

    /**
     * update the prize value for each corresponding jackpot widget
     * @param prizes prize configuration object
     */
    updateValues(prizes: Record<PrizeKey, PrizeConfiguration>) {
        for (const key of Object.keys(prizes) as Array<PrizeKey>) {
            const jackpotKey = key.toLowerCase() as keyof JackpotRecord<unknown>
            const widget = this.jackpots[jackpotKey]

            widget.value = prizes[key].timesStake
        }
    }

    /**
     * reposition widgets on orientation change
     * @param active active transforms
     */
    reposition(active: JackpotTransforms[Orientation]) {
        const keys = Object.keys(this.jackpots)
        for (const key of keys) {
            const jackpotKey = key as keyof JackpotRecord<unknown>
            const widget = this.jackpots[jackpotKey]
            const specification = active[jackpotKey]
            applySpecification(widget, specification)
        }
    }

    displayLanded(ids: number[]) {
        for (const id of ids) {
            const key = this.getJackpotKeyForSymbol(id)

            if (key) {
                this.landed.push(key)
            }
        }

        if (this.landed.length) {
            Object.keys(this.jackpots).forEach((widget) => {
                const key = widget as JackPotKey
                const tint = this.landed.includes(key) ? this.highlight : this.fade
                gsap.to(this.jackpots[key], { duration: 0.2, pixi: { tint } })
            })
        }
    }

    /**
     * get a key that corresponds to a symbol
     * @param id symbol id
     * @returns key or void if the symbol id has an associated widget
     */
    private getJackpotKeyForSymbol(id: number): JackPotKey | void {
        if (Object.hasOwn(this.specification.symbolIds, id)) {
            const property = id as unknown as keyof JackpotWidgetGroupSpec['symbolIds']
            return this.specification.symbolIds[property]
        }
        return
    }

    /** cancel the loop and update the widgets to represent the landed symbols */
    public reset() {
        this.landed = []
        gsap.to(Object.values(this.jackpots), { duration: 0.2, pixi: { tint: this.highlight } })
    }
}

export interface JackpotWidgetGroupSpec extends ContainerSpecification, JackpotRecord<JackpotWidgetSpec> {
    transforms: JackpotTransforms
    symbolIds: Record<number, JackPotKey>
    fadeTint: number
    highlight: number
}

type JackPotKey = 'mini' | 'minor' | 'major' | 'mega'

type JackpotTransforms = OrientationMap<JackpotRecord<IPointData>>

type JackpotRecord<T> = Record<JackPotKey, T>
